<template>
  <div class="store">
    <img class="store__image" src="@images/storeImage.png" alt="image">
    <div class="container">
      <div class="store__wrapper">
        <div class="store__top">
          <el-breadcrumb separator=">" v-if="categories?.split(',').length === 1">
            <el-breadcrumb-item :to="translations.i18nRoute({ name: 'home' })">{{ t('home.title') }}</el-breadcrumb-item>
            <el-breadcrumb-item>{{ activeCategory.name }}</el-breadcrumb-item>
          </el-breadcrumb>
          <h1 class="store__top-title">{{ activeCategory.name ? activeCategory.name : t('store.title') }}</h1>
          <p class="store__top-subtitle" v-if="activeCategory">{{ activeCategory.description }}</p>
        </div>
        <div class="store__line">
          <div class="store__sort">
            <p>{{ t('store.sort.title') }}</p>
            <ui-button
                @click="setNameOrdering"
                :class="[{ active: orderBy.includes('name') }]"
            >
              {{ t('store.sort.name') }}
              <svg :class="[{ rotate: orderBy === '-name' }]" xmlns="http://www.w3.org/2000/svg" width="12" height="17" viewBox="0 0 12 17" fill="none">
                <path d="M5.25 15.4287C5.25 15.8429 5.58579 16.1787 6 16.1787C6.41421 16.1787 6.75 15.8429 6.75 15.4287L5.25 15.4287ZM6.53033 1.1841C6.23744 0.891202 5.76256 0.891202 5.46967 1.1841L0.696699 5.95707C0.403805 6.24996 0.403805 6.72483 0.696699 7.01773C0.989592 7.31062 1.46447 7.31062 1.75736 7.01773L6 2.77509L10.2426 7.01773C10.5355 7.31062 11.0104 7.31062 11.3033 7.01773C11.5962 6.72483 11.5962 6.24996 11.3033 5.95707L6.53033 1.1841ZM6 15.4287L6.75 15.4287L6.75 1.71443L6 1.71443L5.25 1.71443L5.25 15.4287L6 15.4287Z" fill="currentColor"/>
              </svg>
            </ui-button>
            <ui-button
                @click="setPriceOrdering"
                :class="[{ active: orderBy.includes('price') }]"
            >
              {{ t('store.sort.price') }}
              <svg :class="[{ rotate: orderBy === '-price' }]" xmlns="http://www.w3.org/2000/svg" width="12" height="17" viewBox="0 0 12 17" fill="none">
                <path d="M5.25 15.4287C5.25 15.8429 5.58579 16.1787 6 16.1787C6.41421 16.1787 6.75 15.8429 6.75 15.4287L5.25 15.4287ZM6.53033 1.1841C6.23744 0.891202 5.76256 0.891202 5.46967 1.1841L0.696699 5.95707C0.403805 6.24996 0.403805 6.72483 0.696699 7.01773C0.989592 7.31062 1.46447 7.31062 1.75736 7.01773L6 2.77509L10.2426 7.01773C10.5355 7.31062 11.0104 7.31062 11.3033 7.01773C11.5962 6.72483 11.5962 6.24996 11.3033 5.95707L6.53033 1.1841ZM6 15.4287L6.75 15.4287L6.75 1.71443L6 1.71443L5.25 1.71443L5.25 15.4287L6 15.4287Z" fill="currentColor"/>
              </svg>
            </ui-button>
          </div>
          <div class="store__search">
            <img src="@icons/searchIcon.svg" alt="search">
            <input
                type="text"
                v-model="searchInput"
                :placeholder="t('fields.search')"
            >
          </div>
        </div>
        <div class="store__main">
          <div class="store__left">
            <el-affix :offset="50">
              <div class="store__left-inner">
                <ui-filter @filterPrice="onFilterPrice" @filterCategories="onFilterCategories" />
                <img class="store__left-banner" src="@images/storeBanner.png" alt="banner">
              </div>
            </el-affix>
          </div>
          <div class="store__grid" v-if="products.length > 0">
            <product-card
                v-for="product in products"
                :key="product.node.uuid"
                :product="product.node"
            />
            <div class="store__grid-observer" ref="observer"></div>
          </div>
          <div class="store__grid" v-else-if="isLoading">
            <product-card-skeleton v-for="idx in 12" :key="idx" />
          </div>
          <p v-else class="store__empty">{{ t('store.noProducts') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import {useI18n} from "vue-i18n";
import translations from "@/core/helpers/translations.js";
import {Categories} from "@/core/api/categories.js";
import {debounce} from "@/core/helpers/debounce.js";
import UiButton from "@/components/ui/ui-button.vue";
import ProductCard from "@/components/cards/product-card.vue";
import {Products} from "@/core/api/products.js";
import {useIntersectionObserver} from "@vueuse/core";
import ProductCardSkeleton from "@/components/skeletons/product-card-skeleton.vue";
import UiFilter from "@/components/ui/ui-filter.vue";

const {t} = useI18n()
const route = useRoute()

const categories = computed(() => {
  return route.query.categoriesSlugs
})

const activeCategory = ref([])

const getCategory = async () => {
  if (selectedCategories.value.length === 1) {
    const res = await Categories.getCategoryBySlug(selectedCategories.value)
    activeCategory.value = res.data.categories.edges[0].node
  } else {
    activeCategory.value = []
  }
}

const isLoading = ref(false)
const observer = ref(null)

const { stop } = useIntersectionObserver(
    observer,
    ([{ isIntersecting }], observerElement) => {
      if (isIntersecting && pageInfo.value.hasNextPage) {
        getMoreProducts()
      }
    },
)

const selectedCategories = ref(
    route.query.categoriesSlugs
        ? (typeof route.query.categoriesSlugs === 'string'
            ? route.query.categoriesSlugs.split(',')
            : route.query.categoriesSlugs)
        : []
)
const searchInput = ref(route.query['name'] ? (route.query['name']) : '')
const products = ref([])
const pageInfo = ref([])
const minPrice = ref(route.query['minPrice'] ? (route.query['minPrice']) : '')
const maxPrice = ref(route.query['maxPrice'] ? (route.query['maxPrice']) : '')
const cursorForAfter = ref('')
const name = ref(route.query['name'] ? (route.query['name']) : '')
const orderBy = ref(route.query['orderBy'] ? (route.query['orderBy']) : '')

const query = computed(() => {
  const categoriesArray = Array.isArray(selectedCategories.value)
      ? selectedCategories.value
      : (typeof selectedCategories.value === 'string'
          ? [selectedCategories.value]
          : selectedCategories.value ? [selectedCategories.value] : [])

  const categoriesParam = categoriesArray.length
      ? `categoriesSlugs=${categoriesArray.join(',')}`
      : 'categoriesSlugs=';

  let res =`?${categoriesParam}&orderBy=${orderBy.value}&minPrice=${minPrice.value}&maxPrice=${maxPrice.value}`

  if (name.value) {
    res = res + `&name=${name.value}`
  }

  return res
})

const toggleOrdering = (type) => {
  const currentType = orderBy.value.startsWith('-') ? orderBy.value.slice(1) : orderBy.value;
  orderBy.value = (currentType === type) ? (orderBy.value.startsWith('-') ? type : `-${type}`) : type;
};
const setPriceOrdering = () => toggleOrdering('price');
const setNameOrdering = () => toggleOrdering('name');
const onFilterPrice = (filteredPrice) => {
  minPrice.value = filteredPrice[0];
  maxPrice.value = filteredPrice[1];
};
const onFilterCategories = (filteredCategories) => {
  selectedCategories.value = filteredCategories
};

const getProducts = async () => {
  isLoading.value = true
  cursorForAfter.value = '0'

  const res = await Products.getProducts(
      `after: "${cursorForAfter.value}"` +
      `, first: 12` +
      (selectedCategories.value.length ? `, categoriesSlugs: "${selectedCategories.value}"` : "") +
      `, orderBy: "${orderBy.value}"` +
      (minPrice.value ? `, minPrice: "${minPrice.value}"` : "") +
      (maxPrice.value ? `, maxPrice: "${maxPrice.value}"` : "") +
      (name.value ? `, name: "${name.value}"` : "")
  )
  products.value = res.data?.products?.edges
  pageInfo.value = res.data?.products?.pageInfo

  isLoading.value = false
}

const getMoreProducts = async () => {
  isLoading.value = true

  cursorForAfter.value = pageInfo.value.endCursor

  const res = await Products.getProducts(
      `after: "${cursorForAfter.value}"` +
      `, first: 12` +
      (selectedCategories.value.length ? `, categoriesSlugs: "${selectedCategories.value}"` : "") +
      `, orderBy: "${orderBy.value}"` +
      (minPrice.value ? `, minPrice: "${minPrice.value}"` : "") +
      (maxPrice.value ? `, maxPrice: "${maxPrice.value}"` : "") +
      (name.value ? `, name: "${name.value}"` : "")
  )
  products.value.push(...res.data.products.edges)
  pageInfo.value = res.data.products.pageInfo

  isLoading.value = false
}

watch(
    () => query.value,
    async () => {
      products.value = [];
      history.pushState({}, '', route.path + query.value);
      await getProducts();
    }
)

watch(
    () => selectedCategories.value,
    async () => {
      await getCategory();
    },
    { deep: true }
)

const updateNameDebounced = debounce(() => {
  name.value = searchInput.value;
}, 500);

watch(
    () => searchInput.value,
    () => {
      updateNameDebounced();
    }
)

onMounted( async () => {
  await getCategory()
  await getProducts()
})
</script>

<style lang="scss" scoped>
.store {
  padding-block: 100px;
  position: relative;

  &__image {
    position: absolute;
    right: 0;
    top: -65px;
    width: 400px;
  }

  &__top {
    &-title {
      margin-top: 30px;
      color: #F3F3F3;
      font-size: 64px;
      font-weight: 700;
    }

    &-subtitle {
      color: $white;
      font-size: 18px;
    }
  }

  &__line {
    margin-top: 25px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__sort {
    display: flex;
    align-items: center;
    gap: 20px;

    & p {
      flex-shrink: 0;
      color: $white;
      font-size: 20px;
    }

    & button {
      padding-inline: 15px;

      &.active {
        background-color: $white;
        color: $black;
      }

      & svg {
        transition: 0.2s;

        &.rotate {
          transform: rotate(180deg);
        }
      }
    }
  }

  &__search {
    width: 500px;
    border-radius: 10px;
    border: 2px solid $white;
    padding: 8px 13px;
    display: flex;
    align-items: center;
    gap: 10px;
    box-shadow: 0 0 40px 0 rgba(255, 255, 255, 0.46), 1px 1px 6px 0 #FFF;

    & input {
      background-color: transparent;
      width: 100%;

      color: $white;
      font-size: 16px;
    }
  }

  &__main {
    margin-top: 75px;
    display: flex;
    gap: 75px;
  }

  &__left {
    flex-shrink: 0;
    width: 260px;

    &-inner {
      display: flex;
      flex-direction: column;
      gap: 25px;
    }

    &-banner {
      box-shadow: 0 0 10px 1px #F00;
      border-radius: 20px;
      width: 100%;
    }
  }

  &__grid {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(275px, 275px));
    gap: 40px;
    position: relative;
    z-index: 1;
    justify-content: center;

    &-observer {
      width: 100%;
      height: 10px;
      background-color: transparent;
    }
  }
}

@media (max-width: 1100px) {
  .store {
    &__top {
      &-subtitle {
        width: 70%;
      }
    }

    &__line {
      flex-direction: column;
      align-items: flex-start;
      gap: 25px;
    }
  }
}

@media (max-width: 700px) {
  .store {
    &__image {
      display: none;
    }

    &__top {
      &-subtitle {
        width: 100%;
      }
    }

    &__search {
      width: 100%;
    }

    &__main {
      flex-direction: column;
      align-items: center;
    }

    &__left {
      &-inner {
        flex-direction: column-reverse;
      }
    }
  }

  :deep(.el-affix--fixed) {
    position: unset !important;
  }
}

@media (max-width: 500px) {
  .store {
    &__top {
      &-title {
        font-size: 40px;
      }

      &-subtitle {
        font-size: 16px;
      }
    }
  }
}
</style>